import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import UnfoldSalesLogo from './UnfoldSalesLogo';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      if (result.success) {
        // Check for redirect params from email deep links
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect');
        const categoryId = params.get('category_id');
        if (redirect === 'category' && categoryId) {
          localStorage.setItem('pending_redirect', JSON.stringify({ type: 'category', category_id: categoryId }));
        }
        // Clean URL params after storing redirect
        if (params.toString()) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSuccess(true);
    } catch (err) {
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0E0F17] via-gray-900 to-black">
        <div className="max-w-md w-full mx-4">
          <div className="relative p-1 rounded-2xl">
            <div className="absolute inset-0 rounded-2xl border border-blue-500/60 animate-pulse"></div>
            <div className="absolute inset-0 rounded-2xl shadow-[0_0_20px_#3b82f6] opacity-30 animate-pulse"></div>
            
            <div className="relative rounded-2xl shadow-2xl overflow-hidden">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 via-transparent to-blue-500/5 animate-pulse pointer-events-none z-10"></div>

              {/* Blue top section with logo - compact */}
              <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 px-8 pt-8 pb-10 text-center">
                <UnfoldSalesLogo size="xxlarge" className="mx-auto mb-2" />
                <p className="text-blue-200/80 text-xs tracking-widest uppercase">Digitale Schulungsakademie</p>
                <div className="absolute -bottom-6 left-0 right-0 h-12 bg-[#FAFAF8] rounded-t-[2rem]"></div>
              </div>

              {/* Off-white bottom section */}
              <div className="relative bg-[#FAFAF8] px-8 pt-4 pb-8">
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-1">
                  {forgotSuccess ? 'E-Mail gesendet' : 'Passwort vergessen'}
                </h2>
                <p className="text-gray-400 text-center text-sm mb-7">
                  {forgotSuccess 
                    ? 'Prüfen Sie Ihr Postfach' 
                    : 'Geben Sie Ihre E-Mail-Adresse ein'}
                </p>

                {forgotSuccess ? (
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-4 rounded-lg text-center">
                      <svg className="w-12 h-12 mx-auto mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm">
                        Falls ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet. Bitte prüfen Sie auch Ihren Spam-Ordner.
                      </p>
                    </div>
                    <button
                      onClick={() => { setShowForgotPassword(false); setForgotSuccess(false); setForgotEmail(''); setError(''); }}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg active:scale-[0.98]"
                      data-testid="back-to-login-btn"
                    >
                      Zurück zur Anmeldung
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {error}
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail-Adresse</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="ihre@email.de"
                        data-testid="forgot-password-email"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-all shadow-lg active:scale-[0.98]"
                      data-testid="forgot-password-submit"
                    >
                      {forgotLoading ? 'Wird gesendet...' : 'Reset-Link senden'}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(false); setError(''); setForgotEmail(''); }}
                      className="w-full text-gray-400 hover:text-blue-600 py-2 text-sm font-medium transition-colors"
                      data-testid="back-to-login-link"
                    >
                      Zurück zur Anmeldung
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0E0F17] via-gray-900 to-black">
      <div className="max-w-md w-full mx-4">
        {/* Blue Glow Container */}
        <div className="relative p-1 rounded-2xl">
          <div className="absolute inset-0 rounded-2xl border border-blue-500/60 animate-pulse"></div>
          <div className="absolute inset-0 rounded-2xl shadow-[0_0_20px_#3b82f6] opacity-30 animate-pulse"></div>
          
          <div className="relative rounded-2xl shadow-2xl overflow-hidden">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 via-transparent to-blue-500/5 animate-pulse pointer-events-none z-10"></div>

            {/* Blue top section with logo - compact */}
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 px-8 pt-8 pb-10 text-center">
              <UnfoldSalesLogo size="xxlarge" className="mx-auto mb-2" />
              <p className="text-blue-200/80 text-xs tracking-widest uppercase">Digitale Schulungsakademie</p>
              {/* Soft curved transition */}
              <div className="absolute -bottom-6 left-0 right-0 h-12 bg-[#FAFAF8] rounded-t-[2rem]"></div>
            </div>

            {/* Off-white bottom section with form */}
            <div className="relative bg-[#FAFAF8] px-8 pt-4 pb-8">
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-1">Anmelden</h2>
              <p className="text-gray-400 text-center text-sm mb-7">Digitale Schulungsakademie für Door-to-Door-Vertrieb</p>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">E-Mail-Adresse</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="ihre@email.de"
                    data-testid="login-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Passwort</label>
                  <div className="relative">
                    <input
                      type={showLoginPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-4 py-3 pr-12 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Ihr Passwort"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showLoginPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-all shadow-lg active:scale-[0.98]"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Anmelden...' : 'Anmelden'}
                </button>
              </form>

              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setError(''); }}
                  className="text-sm text-gray-400 hover:text-blue-600 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Passwort vergessen?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
